/**
 * Custom useDataPreview Hook
 * Provides Microsoft Graph API data preview functionality
 */

import { useState, useCallback } from 'react';

export interface EmailStats {
  total_emails: number;
  folders: Record<string, { total: number; unread: number; id: string }>;
  inbox_count: number;
  sent_count: number;
  drafts_count: number;
  unread_count: number;
}

export interface CalendarStats {
  total_events: number;
  date_range: {
    start: string;
    end: string;
    days_back: number;
    days_forward: number;
  };
  upcoming_events: number;
  past_events: number;
  all_day_events: number;
  meeting_types: {
    busy: number;
    free: number;
    tentative: number;
    out_of_office: number;
  };
  this_week: number;
  next_week: number;
  this_month: number;
}

export interface ContactsStats {
  total_contacts: number;
}

export interface UserProfile {
  id: string;
  display_name: string;
  email: string;
  job_title?: string;
  office_location?: string;
  business_phones: string[];
  mobile_phone?: string;
}

export interface DataPreview {
  user_profile: UserProfile;
  email_stats: EmailStats;
  calendar_stats: CalendarStats;
  contacts_stats: ContactsStats;
  summary: {
    total_emails: number;
    total_events: number;
    total_contacts: number;
    unread_emails: number;
    upcoming_events: number;
  };
  data_retrieved_at: string;
}

export interface UseDataPreviewReturn {
  // State
  dataPreview: DataPreview | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchDataPreview: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useDataPreview = (): UseDataPreviewReturn => {
  const [dataPreview, setDataPreview] = useState<DataPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset all state
  const reset = useCallback(() => {
    setDataPreview(null);
    setIsLoading(false);
    setError(null);
  }, []);

  // Fetch comprehensive data preview
  const fetchDataPreview = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // TASK-548(c): the Microsoft OAuth token lives under its own key, separate from the app
      // session token (auth_token), so connecting a mailbox never clobbers the advisor's session.
      const oauthToken = localStorage.getItem('ms_oauth_token');

      if (!oauthToken) {
        throw new Error('No Microsoft account connected');
      }

      const response = await fetch('/auth/oauth/microsoft/data-preview', {
        headers: {
          'Authorization': `Bearer ${oauthToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication expired. Please reconnect your account.');
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to fetch data preview: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error('Failed to retrieve data preview');
      }

      setDataPreview(result.data);

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch data preview';
      setError(errorMessage);
      console.error('Data preview error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    // State
    dataPreview,
    isLoading,
    error,

    // Actions
    fetchDataPreview,
    clearError,
    reset
  };
};

export default useDataPreview;